import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import type { Device, ThemeClasses, Brand } from "../types";
import { formatDate } from "../utils/formatters";
import { getStatusLabel, getDeviceTypeLabel } from "../utils/helpers";
import Modal from "../../../../../../components/base/Modal";
import { useIsReseller } from "../../../../../../hooks/useIsReseller";
import { useDisableCRUD } from "../../../../../../store/admin/companiesStore";
import { useRealtimeDeviceData } from "../../../../../../store/admin/vehiclesSocketSelector";
import { useVehiclesSocketStore } from "../../../../../../store/admin/vehiclesSocketStore";
import useIsMonitor from "../../../../../../hooks/useIsMonitor";
interface DeviceDetailsModalProps {
  device: Device;
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  onClose: () => void;
  onEdit: (device: Device) => void;
  allBrands: Brand[];
  t: (key: string, options?: any) => string;
}

export function DeviceDetailsModal({
  device,
  themeClasses,
  isDarkMode,
  onClose,
  onEdit,
  allBrands,
  t,
}: DeviceDetailsModalProps) {
  const { t: tCommon } = useTranslation("common");
  // Buscar el brand asociado al device
  const selectedBrand = allBrands.find((b) => b.id === device.brand_id);
  const { isReseller } = useIsReseller();
  const { isMonitor, isReadOnly } = useIsMonitor();
  const companyDisableCRUD = useDisableCRUD();
  const realtime = useRealtimeDeviceData(device.id);
  const requestAddressForDevice = useVehiclesSocketStore(
    (state) => state.requestAddressForDevice,
  );
  const loadingAddress = useVehiclesSocketStore(
    (state) => !!state.loadingAddressDeviceIds[device.id],
  );

  useEffect(() => {
    if (device.id) {
      requestAddressForDevice(device.id);
    }
  }, [device.id, requestAddressForDevice]);

  const fuelValue =
    realtime.hasData && realtime.fuel != null
      ? realtime.fuel
      : device.fuelLevel;
  const batteryValue =
    realtime.hasData && realtime.battery != null
      ? realtime.battery
      : device.batteryLevel;
  const hasFuelData = fuelValue != null;
  const hasBatteryData = batteryValue != null;

  const getBatteryColor = () => {
    if (!hasBatteryData) return "text-gray-400";
    if (batteryValue! > 80) return "text-[#22C55E]";
    if (batteryValue! > 60) return "text-[#F59E0B]";
    if (batteryValue! > 40) return "text-[#EF4444]";
    return "text-gray-400";
  };

  const getFuelColor = () => {
    if (!hasFuelData) return "text-gray-400";
    if (fuelValue! > 50) return "text-blue-500";
    if (fuelValue! > 25) return "text-amber-500";
    return "text-red-500";
  };

  const inputClassName = `w-full px-3 py-2 ${
    isDarkMode ? "bg-dark-card" : "bg-gray-50"
  } border ${themeClasses.cardBorder} rounded-lg ${
    themeClasses.text
  } focus:outline-none focus:ring-2 focus:ring-primary-500`;

  const selectClassName = `w-full px-3 py-2 ${
    isDarkMode ? "bg-dark-card" : "bg-gray-50"
  } border ${themeClasses.cardBorder} rounded-lg ${
    themeClasses.text
  } focus:outline-none focus:ring-2 focus:ring-primary-500`;

  const isCentralGPS = import.meta.env.VITE_THEME === "central-gps";

  return (
    <Modal
      content={
        <div className="flex flex-col gap-4">
          <div
            className={`${
              isDarkMode ? "bg-dark-card" : "bg-gray-50"
            } p-4 rounded-lg grid grid-cols-1 sm:grid-cols-2 gap-4`}
          >
            <div>
              <p className={`font-semibold ${themeClasses.text}`}>ID</p>
              <p className={`text-sm ${themeClasses.textSecondary}`}>
                {device.id}
              </p>
            </div>
            <div>
              <p className={`font-semibold ${themeClasses.text}`}>
                {t("devices.fields.createdAt")}
              </p>
              <p className={`text-sm ${themeClasses.textSecondary}`}>
                {formatDate(device.createdAt)}
              </p>
            </div>
            <div>
              <p className={`font-semibold ${themeClasses.text}`}>
                {t("devices.fields.updatedAt")}
              </p>
              <p className={`text-sm ${themeClasses.textSecondary}`}>
                {formatDate(device.updatedAt)}
              </p>
            </div>
            <div>
              <p className={`font-semibold ${themeClasses.text}`}>
                {t("devices.fields.lastAddress")}
              </p>
              {loadingAddress ? (
                <div className="flex items-center gap-2 mt-1">
                  <div
                    className={`animate-spin h-3 w-3 border-2 border-t-transparent rounded-full ${isDarkMode ? "border-gray-400" : "border-gray-500"}`}
                  ></div>
                  <span className={`text-sm ${themeClasses.textSecondary}`}>
                    {t("devices.messages.loadingAddress")}
                  </span>
                </div>
              ) : realtime.address ? (
                <p className={`text-sm ${themeClasses.text}`}>
                  {realtime.address}
                </p>
              ) : (
                <p className={`text-sm ${themeClasses.textSecondary}`}>
                  {t("devices.messages.noAddress")}
                </p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="flex flex-col sm:col-span-2">
              <span className={`text-sm font-medium ${themeClasses.text} mb-1`}>
                {t("devices.fields.imei")}
              </span>
              <input
                className={inputClassName}
                type="text"
                value={device.identifier || "N/A"}
                disabled
              />
            </div>

            {/* Cuadro de información del Brand */}
            {selectedBrand && (
              <div
                className={`sm:col-span-2 p-4 rounded-lg border ${isDarkMode ? "bg-dark-card border-gray-700" : "bg-blue-50 border-blue-200"}`}
              >
                <h4
                  className={`text-sm font-semibold mb-3 ${themeClasses.text}`}
                >
                  {t("devices.sections.selectedBrandInfo")}
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div>
                    <p className={`text-xs ${themeClasses.textSecondary}`}>
                      {t("devices.fields.brandName")}
                    </p>
                    <p className={`text-sm font-medium ${themeClasses.text}`}>
                      {selectedBrand.brand}
                    </p>
                  </div>
                  <div>
                    <p className={`text-xs ${themeClasses.textSecondary}`}>
                      {t("devices.fields.model")}
                    </p>
                    <p className={`text-sm font-medium ${themeClasses.text}`}>
                      {selectedBrand.model}
                    </p>
                  </div>
                  <div>
                    <p className={`text-xs ${themeClasses.textSecondary}`}>
                      {t("devices.fields.protocol")}
                    </p>
                    <p className={`text-sm font-medium ${themeClasses.text}`}>
                      {selectedBrand.network?.protocol || "-"}
                    </p>
                  </div>
                  <div>
                    <p className={`text-xs ${themeClasses.textSecondary}`}>
                      {t("devices.fields.ipAddress")}
                    </p>
                    <p className={`text-sm font-medium ${themeClasses.text}`}>
                      {selectedBrand.network?.ip_address || "-"}
                    </p>
                  </div>
                  <div>
                    <p className={`text-xs ${themeClasses.textSecondary}`}>
                      {t("devices.fields.port")}
                    </p>
                    <p className={`text-sm font-medium ${themeClasses.text}`}>
                      {selectedBrand.network?.port || "-"}
                    </p>
                  </div>
                  <div>
                    <p className={`text-xs ${themeClasses.textSecondary}`}>
                      {t("devices.fields.operationalAlias")}
                    </p>
                    <p className={`text-sm font-medium ${themeClasses.text}`}>
                      {selectedBrand.operational_alias || "-"}
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="flex flex-col">
              <span className={`text-sm font-medium ${themeClasses.text} mb-1`}>
                {t("devices.fields.type")}
              </span>
              <select className={selectClassName} disabled>
                <option value="" selected>
                  {getDeviceTypeLabel(device.type, t)}
                </option>
              </select>
            </div>

            <div className="flex flex-col">
              <span className={`text-sm font-medium ${themeClasses.text} mb-1`}>
                {t("devices.fields.status")}
              </span>
              <select className={selectClassName} disabled>
                <option value="" selected>
                  {getStatusLabel(device.status, tCommon)}
                </option>
              </select>
            </div>

            <div className="flex flex-col sm:col-span-2">
              <span className={`text-sm font-medium ${themeClasses.text} mb-1`}>
                {t("devices.fields.vehicleAssigned")}
              </span>
              <select className={selectClassName} disabled>
                <option value="" selected>
                  {device.vehicle_id
                    ? `ID: ${device.vehicle_id}`
                    : t("devices.messages.noVehicleAssigned")}
                </option>
              </select>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:col-span-2">
              <div className="flex flex-col">
                <span
                  className={`text-sm font-medium ${themeClasses.text} mb-1`}
                >
                  <i className="ri-gas-station-line mr-1"></i>
                  {t("devices.fields.fuelLevel")}
                </span>
                <div
                  className={`w-full px-3 py-2 ${isDarkMode ? "bg-dark-card" : "bg-gray-50"} border ${themeClasses.cardBorder} rounded-lg flex items-center justify-between`}
                >
                  <span className={`font-semibold ${getFuelColor()}`}>
                    {hasFuelData
                      ? `${fuelValue}%`
                      : t("devices.messages.noData")}
                  </span>
                  <div
                    className={`w-16 ${isDarkMode ? "bg-gray-600" : "bg-gray-200"} rounded-full h-1.5`}
                  >
                    <div
                      className={`${hasFuelData && fuelValue! > 50 ? "bg-blue-500" : hasFuelData && fuelValue! > 25 ? "bg-amber-500" : hasFuelData && fuelValue! > 0 ? "bg-red-500" : "bg-gray-400"} h-1.5 rounded-full transition-all`}
                      style={{
                        width: `${hasFuelData ? Math.min(fuelValue!, 100) : 0}%`,
                      }}
                    ></div>
                  </div>
                </div>
              </div>

              <div className="flex flex-col">
                <span
                  className={`text-sm font-medium ${themeClasses.text} mb-1`}
                >
                  <i className="ri-battery-line mr-1"></i>
                  {t("devices.fields.batteryLevel")}
                </span>
                <div
                  className={`w-full px-3 py-2 ${isDarkMode ? "bg-dark-card" : "bg-gray-50"} border ${themeClasses.cardBorder} rounded-lg flex items-center justify-between`}
                >
                  <span className={`font-semibold ${getBatteryColor()}`}>
                    {hasBatteryData
                      ? `${Math.round(batteryValue!)}%`
                      : t("devices.messages.noData")}
                  </span>
                  <div
                    className={`w-16 ${isDarkMode ? "bg-gray-600" : "bg-gray-200"} rounded-full h-1.5`}
                  >
                    <div
                      className={`${hasBatteryData && batteryValue! > 80 ? "bg-[#22C55E]" : hasBatteryData && batteryValue! > 60 ? "bg-[#F59E0B]" : hasBatteryData && batteryValue! > 40 ? "bg-[#EF4444]" : "bg-gray-400"} h-1.5 rounded-full transition-all`}
                      style={{
                        width: `${hasBatteryData ? Math.min(batteryValue!, 100) : 0}%`,
                      }}
                    ></div>
                  </div>
                </div>
              </div>

              <div className="flex flex-col">
                <span
                  className={`text-sm font-medium ${themeClasses.text} mb-1`}
                >
                  {t("devices.fields.mileage")}
                </span>
                <input
                  className={inputClassName}
                  type="text"
                  value={
                    realtime.hasData && realtime.odometer != null
                      ? `${Math.round(realtime.odometer).toLocaleString()} km`
                      : device.mileage != null
                        ? `${device.mileage} km`
                        : t("devices.messages.noData")
                  }
                  disabled
                />
              </div>

              <div className="flex flex-col">
                <span
                  className={`text-sm font-medium ${themeClasses.text} mb-1`}
                >
                  {t("devices.fields.hoursMotor")}
                </span>
                <input
                  className={inputClassName}
                  type="text"
                  value={
                    realtime.hasData && realtime.engineHours != null
                      ? `${realtime.engineHours} h`
                      : device.hoursMotor != null
                        ? `${device.hoursMotor} h`
                        : t("devices.messages.noData")
                  }
                  disabled
                />
              </div>
            </div>
          </div>
        </div>
      }
      themeClasses={themeClasses}
      onClose={onClose}
      title={t("devices.modal.detailsTitle")}
      showEditButton={isCentralGPS ? true : !isMonitor && !isReadOnly}
      onEdit={() => onEdit(device)}
      disableCRUD={!isReseller || companyDisableCRUD}
    />
  );
}
